import {
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { DsIcon } from '@descomplica/design-system-react';
import { useViewModel } from '@descomplica/microfrontend-react';
import { ApplicationDto } from 'application/interfaces/pegasus.gateway';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import envs from '@/infrastructure/config/envs';

import { ListIcon } from '../../icon/custom-icon';
import { LogoWrapper } from '../../logo/logo-wrapper';
import { MobileAcessibilityMenu } from '../acessibility-menu/acessibility-menu';
import SelectClassifierMenu from '../select-classifier-menu/select-classifier-menu';
import { DrawerMenuViewModel } from './drawer-menu.vm';
import { DrawerMenuItem } from './drawer-menu-item';
import { DrawerSubmenuItem } from './drawer-submenu-item';

const ProfileUserContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  padding: 16px;
  line-height: 24px;
  letter-spacing: -0.6px;
  word-break: break-word;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
  cursor: pointer;
  border-bottom: 1px solid var(--gray-90);

  & .ds-icon {
    background-color: white !important;
    width: 24px;
    height: 24px;
  }
`;

const ProfileUserRightContainer = styled.div`
  display: flex;
  flex-direction: column;

  & h2 {
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: -0.6px;
  }
  & p {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: -0.6px;
    color: #96999c;
  }
`;

const ProfileUser = () => {
  const vm = useViewModel(DrawerMenuViewModel);

  return (
    <ProfileUserContainer>
      <DsIcon image="account" />
      <ProfileUserRightContainer>
        <h2>{vm.user?.name}</h2>
        <p>{vm.user?.email}</p>
      </ProfileUserRightContainer>
    </ProfileUserContainer>
  );
};

export default function DrawerMenu({ application }: { application: ApplicationDto }): JSX.Element {
  const vm = useViewModel(DrawerMenuViewModel);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();
  const navigate = useNavigate();

  const openStudyPlan = () => {
    navigate('/');
    onClose();
  };

  const openPractice = () => {
    navigate('/practice');
    onClose();
  };

  const openLibrary = () => {
    navigate('/library');
    onClose();
  };

  const openAccount = () => {
    window.location.href = envs.recognitionAccountManagementUrl;
  };

  const openAccountPassword = () => {
    window.location.href = envs.recognitionPasswordManagementUrl;
  };

  const logout = async () => {
    await vm.clearAccount();
    window.location.href = envs.recognitionSigninUrl;
  };

  return (
    <>
      <IconButton
        aria-label="abrir menu de navegação mobile"
        ref={btnRef}
        onClick={onOpen}
        backgroundColor="transparent"
        _hover={{ bg: 'var(--gray-90)', color: 'white' }}
        icon={<ListIcon />}
      />
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} finalFocusRef={btnRef}>
        <DrawerOverlay />
        <DrawerContent backgroundColor={'var(--header-background-primary)'}>
          <DrawerHeader padding={0}>
            <DrawerCloseButton />
            <ProfileUser />
          </DrawerHeader>
          <Flex justifyContent={'start'} padding={'16px 16px 0'}>
            <LogoWrapper application={application} />
          </Flex>
          <DrawerBody padding={0}>
            <Box padding="16px">
              <SelectClassifierMenu />
              <Divider orientation="horizontal" borderColor={'var(--gray-90)'} margin="16px 0 0" />
              {vm.shouldShowStudyPlanTab && (
                <DrawerMenuItem onClick={openStudyPlan} text="Minha Jornada" />
              )}
              <DrawerMenuItem onClick={openLibrary} text="Cursos" />
              {vm.shouldShowPracticeTab && (
                <DrawerSubmenuItem text="Treinar">
                  <DrawerMenuItem text="Simulados" onClick={openPractice} />
                </DrawerSubmenuItem>
              )}
            </Box>
            <Divider orientation="horizontal" borderColor={'var(--gray-90)'} />
            <Box padding="16px">
              <DrawerMenuItem text="Dados Pessoais" onClick={openAccount} />
              <DrawerMenuItem text="Alterar Senha" onClick={openAccountPassword} />
              <DrawerMenuItem text="Sair" onClick={logout} />
            </Box>
          </DrawerBody>

          <DrawerFooter>
            <MobileAcessibilityMenu />
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
